import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AuditTrail } from '@/components/AuditTrail';
import { Badge, Callout, Card, DefinitionList, SectionHeading } from '@/components/ui';
import { formatDate, formatDateTime, formatTime } from '@/domain/dates';
import { formatEur, formatKm, vatFromGross } from '@/domain/money';
import type { Order, Trip } from '@/domain/types';
import { orderRepo, tripRepo } from '@/db/repositories';
import { useSession } from '@/session/SessionContext';
import { passengerDisplayName } from '@/suggest';
import { useT } from '@/i18n';

export function TripDetailScreen() {
  const session = useSession();
  const t = useT();
  const { tripId } = useParams<{ tripId: string }>();
  const [trip, setTrip] = useState<Trip | null>(null);
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tripId) return;
    void (async () => {
      const found = await tripRepo.get(session.dataKey, tripId);
      setTrip(found ?? null);
      if (found?.orderId) {
        setOrder((await orderRepo.get(session.dataKey, found.orderId)) ?? null);
      }
      setLoading(false);
    })();
  }, [tripId, session.dataKey]);

  if (loading) return <p className="text-muted text-sm">{t.common.loading}</p>;
  if (!trip) return <Callout tone="danger">{t.trips.notFound}</Callout>;

  const passenger = session.passengers.find((p) => p.id === trip.passengerId);
  const fromAddress = session.addresses.find((a) => a.id === trip.fromAddressId);
  const toAddress = session.addresses.find((a) => a.id === trip.toAddressId);
  const vehicle = session.vehicles.find((v) => v.id === trip.vehicleId);
  const vatCents = vatFromGross(trip.fareGrossCents, trip.vatRatePercent);

  return (
    <div className="space-y-5">
      <SectionHeading
        title={t.trips.detailTitle(formatDate(trip.date))}
        description={passenger ? passengerDisplayName(passenger) : undefined}
        action={
          <Link to={`/fahrten/${trip.id}/bearbeiten`} className="btn-secondary">
            {t.trips.edit}
          </Link>
        }
      />

      <div className="flex flex-wrap gap-2">
        {trip.lockedAt && (
          <Badge tone="info">{t.trips.lockedOn(formatDate(trip.lockedAt.slice(0, 10)))}</Badge>
        )}
        {trip.distanceOverridden && <Badge tone="warning">{t.trips.distanceFixed}</Badge>}
        {trip.invoiceId ? (
          <Badge tone="success">{t.trips.badgeInvoiced}</Badge>
        ) : (
          <Badge>{t.trips.badgeOpen}</Badge>
        )}
      </div>

      <Card title={t.trips.sectionTrip}>
        <DefinitionList
          items={[
            {
              term: t.trips.passenger,
              value: passenger ? passengerDisplayName(passenger) : t.common.dash,
            },
            { term: t.trips.fromLabel, value: fromAddress?.label ?? t.common.dash },
            { term: t.trips.toLabel, value: toAddress?.label ?? t.common.dash },
            {
              term: t.trips.times,
              value: `${formatTime(trip.startedAt)} – ${formatTime(trip.endedAt)}`,
            },
            {
              term: t.trips.route,
              value: (
                <>
                  {formatKm(trip.distanceMeters)} km
                  <span className="text-muted">
                    {' '}
                    · {t.labels.distanceSource[trip.distanceSource]}
                  </span>
                  {trip.computedDistanceMeters !== undefined &&
                    trip.computedDistanceMeters !== trip.distanceMeters && (
                      <span className="text-muted">
                        {' '}
                        {t.trips.computedWas(formatKm(trip.computedDistanceMeters))}
                      </span>
                    )}
                </>
              ),
            },
            { term: t.trips.tripType, value: t.labels.tripType[trip.tripType] },
            {
              term: t.trips.vehicle,
              value: vehicle
                ? `${vehicle.vehicleCode} · ${vehicle.licensePlate}`
                : t.common.dash,
            },
          ]}
        />
      </Card>

      <Card title={t.trips.sectionBilling}>
        <DefinitionList
          items={[
            { term: t.trips.fareGross, value: formatEur(trip.fareGrossCents) },
            {
              term: t.trips.vatOfWhich,
              value: `${formatEur(vatCents)} (${trip.vatRatePercent} %)`,
            },
            { term: t.trips.net, value: formatEur(trip.fareGrossCents - vatCents) },
            { term: t.trips.tip, value: trip.tipCents ? formatEur(trip.tipCents) : t.common.dash },
            {
              term: t.trips.paymentMethod,
              value: t.labels.payment[trip.paymentMethod] ?? trip.paymentMethod,
            },
          ]}
        />
      </Card>

      {(order || trip.comment) && (
        <Card title={t.trips.sectionOrder}>
          <DefinitionList
            items={[
              ...(order
                ? [
                    { term: t.trips.orderNumber, value: String(order.sequenceNumber) },
                    { term: t.trips.orderReceived, value: formatDateTime(order.receivedAt) },
                    { term: t.trips.orderChannel, value: t.labels.channel[order.channel] },
                    {
                      term: t.trips.orderAccepted,
                      value: order.acceptancePlace
                        ? `${t.labels.acceptance[order.acceptancePlace]}${
                            order.acceptedAt ? `, ${formatDateTime(order.acceptedAt)}` : ''
                          }`
                        : t.trips.orderNotAccepted,
                    },
                  ]
                : []),
              ...(trip.comment ? [{ term: t.trips.comment, value: trip.comment }] : []),
            ]}
          />
        </Card>
      )}

      <Card
        title={t.trips.changeLog}
        action={<span className="text-muted text-xs">BFH VI B 37/23</span>}
      >
        <p className="text-muted mb-4 text-xs leading-relaxed">{t.trips.changeLogNote}</p>
        <AuditTrail entityId={trip.id} />
      </Card>
    </div>
  );
}
